import { c as _c } from "react/compiler-runtime";
import { useContext, useState } from 'react';
import { Route } from 'react-router-dom';
import useSWR from 'swr';
import { organization_levels_by_role } from '../../../../../../../../domain/roles';
import { OrganizationMember, OrganizationRole } from '../../../../../../../../typings/OrganizationMember.interface';
import APIMethodKeys from '../../../../../client/APIMethodKeys';
import { capitalizeFirstLetter } from '../../../../../utils';
import Alert from '../../../../common/base/Alert';
import Badge from '../../../../common/base/Badge';
import Button from '../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../common/base/Card';
import Container from '../../../../common/base/Container';
import Icon from '../../../../common/base/Icon';
import Tabs from '../../../../common/base/Tabs';
import Text from '../../../../common/base/Text';
import Tooltip from '../../../../common/base/Tooltip';
import { useDialog } from '../../../../common/Dialog';
import DropdownMenu from '../../../../common/DropdownMenu';
import { Div } from '../../../../common/helpers/StyledUtils';
import Search from '../../../../common/Search';
import { useToasts } from '../../../../common/Toast';
import { WithOrganizationAdminRole } from '../../../../common/WithRole';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { UserContext } from '../../../../contexts/UserContext';
import { useMemberTrial } from '../../../../hooks/useMemberTrial';
import useSearchQuery from '../../../../hooks/useSearchQuery';
import { DashboardContext } from '../../DashboardContext';
import AddOrganizationMemberModal from './AddOrganizationMemberModal';
const role_options = {
  owner: {
    label: 'Owner',
    description: 'Unrestricted access. Selecting this role will transfer your ownership.'
  },
  admin: {
    label: 'Admin',
    description: 'Unrestricted access to everything, excluding ownership transfer.'
  },
  member: {
    label: 'Member',
    description: 'Read and write access to projects, can only view organization members & billing.'
  },
  viewer: {
    label: 'Viewer',
    description: 'Read-only access to projects,can only view organization members & billing.'
  }
};
export const createRoleOptions = (viewer_enabled: boolean, can_transfer_ownership: boolean) => Object.keys(role_options).filter((role: OrganizationRole) => role !== 'owner' || can_transfer_ownership).map((role: OrganizationRole) => ({
  value: role,
  label: role_options[role].label,
  description: role_options[role].description,
  disabled: role === 'viewer' && !viewer_enabled,
  tooltip: role === 'viewer' && !viewer_enabled ? 'Upgrade plan to enable role' : undefined
}));
const OrganizationMembers = () => {
  const $ = _c(116);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    user
  } = useContext(UserContext);
  const {
    subscription,
    mutateSubscription,
    organization,
    organization_role,
    resetContext
  } = useContext(DashboardContext);
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  const [search_value, setSearchValue] = useState("");
  let t0;
  if ($[0] !== organization.id) {
    t0 = APIMethodKeys.organizations.listMembers(organization.id);
    $[0] = organization.id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== HookdeckAPI.organizations) {
    t1 = () => HookdeckAPI.organizations.listMembers();
    $[2] = HookdeckAPI.organizations;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data: org_members,
    mutate: mutateMembers
  } = useSWR(t0, t1);
  let t2;
  if ($[4] !== org_members || $[5] !== user) {
    t2 = org_members?.find(m => m.user_id === user.id)?.role;
    $[4] = org_members;
    $[5] = user;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const user_role = t2;
  let t3;
  if ($[7] !== organization.id) {
    t3 = APIMethodKeys.organizations.listMemberInvites(organization.id);
    $[7] = organization.id;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== HookdeckAPI.organizations) {
    t4 = () => HookdeckAPI.organizations.listMemberInvites();
    $[9] = HookdeckAPI.organizations;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const {
    data: invites,
    mutate: mutateInvites
  } = useSWR(t3, t4);
  const {
    is_member_trial_active,
    is_member_trial_expired,
    member_trial_left_days
  } = useMemberTrial(subscription);
  let T0;
  let T1;
  let T2;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[11] !== HookdeckAPI.billing || $[12] !== HookdeckAPI.organizations || $[13] !== addToast || $[14] !== invites || $[15] !== is_member_trial_active || $[16] !== is_member_trial_expired || $[17] !== member_trial_left_days || $[18] !== mutateInvites || $[19] !== mutateMembers || $[20] !== mutateSubscription || $[21] !== org_members || $[22] !== organization.directory_sync_enabled || $[23] !== organization.sso_enabled || $[24] !== organization_role || $[25] !== query.tab || $[26] !== resetContext || $[27] !== search_value || $[28] !== showDialog || $[29] !== subscription.features || $[30] !== subscription.max_users || $[31] !== subscription.outpost_plan || $[32] !== subscription.team_member_trial_start_at || $[33] !== updateSearchQuery || $[34] !== user || $[35] !== user_role) {
    const changeOwnerRole = async id => {
      const member = org_members?.find(m_0 => m_0.id === id);
      showDialog(() => changeUserRole(id, "owner"), _temp, {
        title: "Transfer Ownership",
        message: <Text>Once you transfer ownership to{" "}<Text as="span" bold={true}>{member?.user_name}</Text>, you will be made an Admin, with unrestricted access to all project actions except ownership management. After transferring your ownership,{" "}<Text as="span" bold={true}>{member?.user_name}</Text>{" "}will be the sole Owner of this project.</Text>,
        submit_label: "Transfer Ownership",
        cancel_label: "Cancel"
      });
    };
    const changeUserRole = async (id_0, role) => {
      ;
      try {
        await HookdeckAPI.organizations.updateRole(id_0, role);
        mutateMembers(members => {
          if (role === "owner") {
            const owner_member = members?.find(_temp2);
            if (owner_member) {
              owner_member.role = "admin";
            }
          }
          return members?.map(m_2 => m_2.id === id_0 ? {
            ...m_2,
            role
          } : m_2);
        }, false);
        addToast("success", `Role changed to ${capitalizeFirstLetter(role)}`);
      } catch (t17) {
        const err = t17;
        addToast("error", err.message);
      }
    };
    const changeRole = async (id_1, role_0) => {
      if (role_0 === "owner") {
        changeOwnerRole(id_1);
      } else {
        changeUserRole(id_1, role_0);
      }
    };
    let t18;
    if ($[51] !== HookdeckAPI.organizations || $[52] !== addToast || $[53] !== mutateMembers || $[54] !== showDialog) {
      t18 = (id_2, email) => {
        showDialog(async () => {
          ;
          try {
            await HookdeckAPI.organizations.removeMember(id_2);
            mutateMembers(t20 => {
              let members_0 = t20 === undefined ? [] : t20;
              members_0 = members_0?.filter(m_3 => m_3.id !== id_2) ?? [];
              return members_0;
            }, false);
            addToast("success", "Member removed from organization");
          } catch (t19) {
            const err_0 = t19;
            addToast("error", err_0.message);
          }
        }, _temp3, {
          title: "Remove from organization",
          message: <Text><Text as="span" bold={true}>{email}</Text>{" "}will lose access to this organization and all its projects. Are you sure?</Text>,
          submit_label: "Remove from organization",
          cancel_label: "Cancel",
          danger: true
        });
      };
      $[51] = HookdeckAPI.organizations;
      $[52] = addToast;
      $[53] = mutateMembers;
      $[54] = showDialog;
      $[55] = t18;
    } else {
      t18 = $[55];
    }
    const handleRemove = t18;
    let t19;
    if ($[56] !== HookdeckAPI.organizations || $[57] !== addToast || $[58] !== resetContext || $[59] !== showDialog) {
      t19 = () => {
        showDialog(async () => {
          ;
          try {
            await HookdeckAPI.organizations.leave();
            resetContext();
          } catch (t20) {
            const err_1 = t20;
            addToast("error", err_1.message);
          }
        }, _temp4, {
          title: "Leave organization",
          message: <Text>You will lose access to this organization and all of the projects within it. If you’d like to re-join, the organization owner or an admin will need to re-invite you. Are you sure?</Text>,
          submit_label: "Leave organization",
          cancel_label: "Stay in organization",
          danger: true
        });
      };
      $[56] = HookdeckAPI.organizations;
      $[57] = addToast;
      $[58] = resetContext;
      $[59] = showDialog;
      $[60] = t19;
    } else {
      t19 = $[60];
    }
    const handleLeave = t19;
    const exceeds_users = subscription.max_users && org_members && org_members.length >= subscription.max_users;
    const invite_to_trial = !!exceeds_users && !organization?.sso_enabled;
    const members_1 = org_members?.map(member_0 => {
      if (!exceeds_users || is_member_trial_active || !is_member_trial_expired || member_0.role === "owner") {
        member_0.status = "ACTIVE";
      } else {
        member_0.status = "TRIAL_EXPIRED";
      }
      return member_0;
    }) || [];
    const filtered_members = search_value ? members_1?.filter(member_1 => member_1.user_name.toLowerCase().includes(search_value.toLowerCase()) || member_1.user_email.toLowerCase().includes(search_value.toLowerCase())) : members_1;
    const filtered_invites = search_value ? invites?.filter(inv => inv.email.toLowerCase().includes(search_value.toLowerCase())) : invites;
    const has_members_with_outpost_access_only = !is_member_trial_active && !is_member_trial_expired && subscription.outpost_plan && subscription.max_users && members_1.length > 1;
    const can_offer_member_trial = has_members_with_outpost_access_only && !subscription.team_member_trial_start_at;
    T2 = Container;
    t14 = true;
    t15 = true;
    if ($[61] !== addToast || $[62] !== mutateInvites || $[63] !== mutateMembers || $[64] !== mutateSubscription) {
      t16 = <Route path="/settings/organization/members/add" render={() => <AddOrganizationMemberModal onMembersAdded={(new_members, new_invites, subscription_0) => {
        mutateMembers(members_2 => {
          new_members.forEach(member_2 => {
            if (!members_2?.some(m_4 => m_4.user_id === member_2.user_id)) {
              members_2 = [member_2, ...(members_2 ?? [])];
            }
          });
          return members_2;
        }, false);
        mutateInvites(invites_0 => {
          new_invites.forEach(invite => {
            if (!invites_0?.some(i => i.email === invite.email)) {
              invites_0?.push(invite);
            }
          });
          return invites_0;
        }, false);
        mutateSubscription(subscription_0);
        addToast("success", "Members invited");
      }} />} />;
      $[61] = addToast;
      $[62] = mutateInvites;
      $[63] = mutateMembers;
      $[64] = mutateSubscription;
      $[65] = t16;
    } else {
      t16 = $[65];
    }
    T1 = Div;
    if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        b: 10
      };
      $[66] = t8;
    } else {
      t8 = $[66];
    }
    let t20;
    let t21;
    if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = {
        b: 4
      };
      t21 = {
        justify: "space-between",
        align: "center"
      };
      $[67] = t20;
      $[68] = t21;
    } else {
      t20 = $[67];
      t21 = $[68];
    }
    let t22;
    if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <Div><Text bold={true} size="l" as="h2" m={{
          y: 0
        }}>Organization members</Text><Text muted={true}>All organization members can access open projects.</Text></Div>;
      $[69] = t22;
    } else {
      t22 = $[69];
    }
    let t23;
    if ($[70] !== exceeds_users || $[71] !== invite_to_trial || $[72] !== is_member_trial_expired || $[73] !== organization.directory_sync_enabled) {
      t23 = !organization?.directory_sync_enabled && <Div><Button.Permission role="admin" unauthorized_tooltip="Only Owners and Admins can add members." to={_temp5} disabled={!!exceeds_users && !invite_to_trial || is_member_trial_expired} primary={true} icon="add">Add members</Button.Permission></Div>;
      $[70] = exceeds_users;
      $[71] = invite_to_trial;
      $[72] = is_member_trial_expired;
      $[73] = organization.directory_sync_enabled;
      $[74] = t23;
    } else {
      t23 = $[74];
    }
    if ($[75] !== t23) {
      t9 = <Div m={t20} flex={t21}>{t22}{t23}</Div>;
      $[75] = t23;
      $[76] = t9;
    } else {
      t9 = $[76];
    }
    if ($[77] !== organization.sso_enabled) {
      t10 = organization?.sso_enabled && <Alert inline={true} info={true} m={{
        b: 4
      }}>Your organization is configured to use SSO and access for organisation members is managed through your identity provider.<WithOrganizationAdminRole>{" "}You can still invite manually members outside your organization.</WithOrganizationAdminRole></Alert>;
      $[77] = organization.sso_enabled;
      $[78] = t10;
    } else {
      t10 = $[78];
    }
    if ($[79] !== HookdeckAPI.billing || $[80] !== addToast || $[81] !== can_offer_member_trial || $[82] !== mutateMembers || $[83] !== mutateSubscription) {
      t11 = can_offer_member_trial && <Alert neutral={true} inline={true} m={{
        b: 4
      }} buttonPrimary={true} action={{
        label: "Start trial",
        onClick: () => {
          HookdeckAPI.billing.startTeamMemberTrial().then(subscription_1 => {
            mutateMembers();
            addToast("success", "Event Gateway trial started");
            mutateSubscription(subscription_1);
          });
        }
      }}>Grant Event Gateway projects access to your organization members with a 14-day trial.</Alert>;
      $[79] = HookdeckAPI.billing;
      $[80] = addToast;
      $[81] = can_offer_member_trial;
      $[82] = mutateMembers;
      $[83] = mutateSubscription;
      $[84] = t11;
    } else {
      t11 = $[84];
    }
    if ($[85] !== is_member_trial_active || $[86] !== member_trial_left_days) {
      t12 = is_member_trial_active && <Alert inline={true} warning={true} buttonPrimary={true} m={{
        b: 4
      }} action={{
        icon: "upgrade",
        label: "Upgrade plan",
        to: "/settings/organization/plans?highlight=max_users"
      }}><Text bold={true}>Invited members will lose access to this organization Event Gateway projects in{" "}{member_trial_left_days} days</Text><Text>Upgrade your plan to allow all users to access all projects indefinitely.</Text></Alert>;
      $[85] = is_member_trial_active;
      $[86] = member_trial_left_days;
      $[87] = t12;
    } else {
      t12 = $[87];
    }
    if ($[88] !== is_member_trial_expired) {
      t13 = is_member_trial_expired && <Alert inline={true} warning={true} buttonPrimary={true} m={{
        b: 4
      }} action={{
        icon: "upgrade",
        label: "Upgrade plan",
        to: "/settings/organization/plans?highlight=max_users"
      }}><Text bold={true}>Free user trial has ended</Text><Text>Invited users have limited access to this organization Event Gateway projects. Upgrade your plan to allow all users to access all projects indefinitely.</Text></Alert>;
      $[88] = is_member_trial_expired;
      $[89] = t13;
    } else {
      t13 = $[89];
    }
    T0 = StyledCard;
    let t24;
    let t25;
    if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = {
        x: 4,
        t: 3
      };
      t25 = {
        justify: "space-between",
        align: "flex-start"
      };
      $[90] = t24;
      $[91] = t25;
    } else {
      t24 = $[90];
      t25 = $[91];
    }
    let t26;
    if ($[92] !== updateSearchQuery) {
      t26 = tab => updateSearchQuery({
        tab
      });
      $[92] = updateSearchQuery;
      $[93] = t26;
    } else {
      t26 = $[93];
    }
    let t27;
    if ($[94] !== search_value) {
      t27 = <Search value={search_value} onChange={setSearchValue} small={true} />;
      $[94] = search_value;
      $[95] = t27;
    } else {
      t27 = $[95];
    }
    t5 = <StyledCardSection p={t24} flex={t25}><Tabs onTabSelected={t26} active_tab={query.tab ?? "members"} tabs={[{
        label: "Members",
        key: "members",
        badge_label: members_1?.length,
        badge_theme: "primary"
      }, {
        label: "Invited",
        key: "invited",
        badge_label: invites && invites.length > 0 ? invites.length : undefined,
        badge_theme: "muted"
      }]} border={false} />{t27}</StyledCardSection>;
    t6 = query.tab !== "invited" && <>{filtered_members?.sort(_temp6).map((member_3, i_0) => <StyledCardSection p={{
        x: 4,
        y: 3
      }} flex={{
        align: "center",
        gap: 4
      }} key={member_3.id}><Div w={50}><Div flex={{
            align: "center",
            gap: 2
          }}><Text semi={true} ellipsis={true}>{member_3.user_name}{member_3.user_id === user.id && " (You)"}</Text>{is_member_trial_active && member_3.role !== "owner" && <Badge neutral={true} subtle={true}>Trial access</Badge>}{member_3.status === "TRIAL_EXPIRED" && <Badge warning={true} subtle={true}>Trial access expired</Badge>}{member_3.mfa_enabled && <Tooltip tooltip="Multi-factor authentication is enabled for this member"><Badge success={true} subtle={true}>MFA</Badge></Tooltip>}{has_members_with_outpost_access_only && member_3.role !== "owner" && <Tooltip tooltip="This member has access to Outpost projects only, upgrade your Event Gateway plan to allow access to all projects" side="bottom"><Badge neutral={true} subtle={true}>Outpost-only</Badge></Tooltip>}</Div><Text muted={true} ellipsis={true}>{member_3.user_email}</Text></Div><Div w={50}>{user.id !== member_3.user_id && member_3.role !== "owner" && organization_levels_by_role[organization_role] >= organization_levels_by_role.admin ? <DropdownMenu placement="bottom-start" outline={true} w={{
            px: 272
          }} options_max_height={{
            px: 350
          }} label={capitalizeFirstLetter(member_3.role)} options={createRoleOptions(!!subscription?.features?.includes("viewer_role"), user_role === "owner").map(option => ({
            ...option,
            onClick: () => changeRole(member_3.id, option.value)
          }))} /> : <Button outline={true} disabled={true}>{capitalizeFirstLetter(member_3.role)}</Button>}</Div><Div w={{
          px: 32
        }}>{user.id === member_3.user_id && member_3.role !== "owner" && !user.sso_idp_id && <Div flex={{
            justify: "flex-end"
          }}><DropdownMenu placement="bottom-end" ghost={true} icon="horizontal_more" options={[{
              icon: "leave",
              label: "Leave Organization",
              onClick: handleLeave,
              danger: true
            }]} /></Div>}{user.id !== member_3.user_id && member_3.role !== "owner" && <WithOrganizationAdminRole><Button ghost={true} icon="delete" onClick={() => handleRemove(member_3.id, member_3.user_email)} /></WithOrganizationAdminRole>}</Div></StyledCardSection>)}{filtered_members && filtered_members.length === 0 && <StyledCardSection p={4}><Text muted={true}><Icon icon="info" muted={true} left={true} />No matching users found.</Text></StyledCardSection>}</>;
    t7 = query.tab === "invited" && <>{invites && invites.length > 0 && <>{filtered_invites?.sort(_temp7).map(invite_0 => <StyledCardSection p={{
          x: 4,
          y: 3
        }} flex={{
          justify: "space-between",
          align: "center"
        }} key={invite_0.id}><Div flex={{
            gap: 2
          }} w={60}><Text dark={true} semi={true}>{invite_0.user?.name || invite_0.email}</Text><Badge neutral={true}>{invite_0.status === "EXPIRED" ? "Expired" : "Invited"}</Badge></Div><Div w={40}><Button outline={true} disabled={true}>{capitalizeFirstLetter(invite_0.role || "member")}</Button></Div><Div flex={{
            align: "center",
            gap: 3
          }} w={{
            px: 134
          }}><WithOrganizationAdminRole><Button disabled={is_member_trial_expired} onClick={() => {
                HookdeckAPI.organizations.resendInvite(invite_0.id).then(invite_1 => {
                  mutateInvites(invites_1 => {
                    if (invites_1 && invites_1.length > 0) {
                      const i_1 = invites_1.findIndex(inv_0 => inv_0.id === invite_1.id);
                      invites_1[i_1] = invite_1;
                    }
                    return invites_1;
                  });
                  addToast("success", `Invite to ${invite_1.email} resent.`);
                }).catch(e => {
                  addToast("error", e.response?.message || "Failed to resend invite");
                });
              }} ghost={true} icon="retry">Resend</Button><Tooltip disabled={is_member_trial_expired} tooltip="Revoke invite" align="right"><Button disabled={is_member_trial_expired} onClick={() => {
                  HookdeckAPI.organizations.revokeInvite(invite_0.id).then(t28 => {
                    const {
                      id: id_3
                    } = t28;
                    mutateInvites(invites_2 => {
                      invites_2 = invites_2?.filter(inv_1 => inv_1.id !== id_3) ?? [];
                      if (invites_2.length === 0) {
                        return;
                      }
                      return invites_2;
                    });
                    addToast("success", `Invite to ${invite_0.email} revoked.`);
                  });
                }} ghost={true} icon="cancel" /></Tooltip></WithOrganizationAdminRole></Div></StyledCardSection>)}</>}{filtered_invites && filtered_invites.length === 0 && invites && invites.length > 0 && <StyledCardSection p={4}><Text muted={true}><Icon icon="info" muted={true} left={true} />No matching invites found.</Text></StyledCardSection>}{invites && invites.length === 0 && <StyledCardSection p={4}><Text muted={true}><Icon icon="info" muted={true} left={true} />There are no pending invitations</Text></StyledCardSection>}</>;
    $[11] = HookdeckAPI.billing;
    $[12] = HookdeckAPI.organizations;
    $[13] = addToast;
    $[14] = invites;
    $[15] = is_member_trial_active;
    $[16] = is_member_trial_expired;
    $[17] = member_trial_left_days;
    $[18] = mutateInvites;
    $[19] = mutateMembers;
    $[20] = mutateSubscription;
    $[21] = org_members;
    $[22] = organization.directory_sync_enabled;
    $[23] = organization.sso_enabled;
    $[24] = organization_role;
    $[25] = query.tab;
    $[26] = resetContext;
    $[27] = search_value;
    $[28] = showDialog;
    $[29] = subscription.features;
    $[30] = subscription.max_users;
    $[31] = subscription.outpost_plan;
    $[32] = subscription.team_member_trial_start_at;
    $[33] = updateSearchQuery;
    $[34] = user;
    $[35] = user_role;
    $[36] = T0;
    $[37] = T1;
    $[38] = T2;
    $[39] = t10;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
    $[46] = t5;
    $[47] = t6;
    $[48] = t7;
    $[49] = t8;
    $[50] = t9;
  } else {
    T0 = $[36];
    T1 = $[37];
    T2 = $[38];
    t10 = $[39];
    t11 = $[40];
    t12 = $[41];
    t13 = $[42];
    t14 = $[43];
    t15 = $[44];
    t16 = $[45];
    t5 = $[46];
    t6 = $[47];
    t7 = $[48];
    t8 = $[49];
    t9 = $[50];
  }
  let t17;
  if ($[96] !== T0 || $[97] !== t5 || $[98] !== t6 || $[99] !== t7) {
    t17 = <T0>{t5}{t6}{t7}</T0>;
    $[96] = T0;
    $[97] = t5;
    $[98] = t6;
    $[99] = t7;
    $[100] = t17;
  } else {
    t17 = $[100];
  }
  let t18;
  if ($[101] !== T1 || $[102] !== t10 || $[103] !== t11 || $[104] !== t12 || $[105] !== t13 || $[106] !== t17 || $[107] !== t8 || $[108] !== t9) {
    t18 = <T1 m={t8}>{t9}{t10}{t11}{t12}{t13}{t17}</T1>;
    $[101] = T1;
    $[102] = t10;
    $[103] = t11;
    $[104] = t12;
    $[105] = t13;
    $[106] = t17;
    $[107] = t8;
    $[108] = t9;
    $[109] = t18;
  } else {
    t18 = $[109];
  }
  let t19;
  if ($[110] !== T2 || $[111] !== t14 || $[112] !== t15 || $[113] !== t16 || $[114] !== t18) {
    t19 = <T2 large={t14} left={t15}>{t16}{t18}</T2>;
    $[110] = T2;
    $[111] = t14;
    $[112] = t15;
    $[113] = t16;
    $[114] = t18;
    $[115] = t19;
  } else {
    t19 = $[115];
  }
  return t19;
};
export default OrganizationMembers;
function _temp() {
  return null;
}
function _temp2(m_1) {
  return m_1.role === "owner";
}
function _temp3() {
  return null;
}
function _temp4() {
  return null;
}
function _temp5(location) {
  return {
    ...location,
    pathname: "/settings/organization/members/add",
    state: {
      scroll: false
    }
  };
}
function _temp6(a, b) {
  return a.created_at > b.created_at ? -1 : 1;
}
function _temp7(a_0, b_0) {
  return a_0.created_at > b_0.created_at ? -1 : 1;
}